import type { TourState } from '@fint/validation';

/**
 * The guided tours (requests.md R-07), and the rules for offering them.
 *
 * ── WHAT A STEP IS ──
 * A place on the screen and two sentences about it. `target` is a CSS
 * selector; the first VISIBLE match is the one lit, so one step works on the
 * wide sidebar, the icon rail and a phone (where the sidebar is hidden and the
 * card simply sits in the middle). `needs` is the permission the thing behind
 * it takes: a step for a page somebody's role cannot open is left out, so
 * every person's tour is their own product and nothing it shows refuses them.
 *
 * ── THE RULES ──
 * - The welcome tour is offered when it has never been taken, and again six
 *   months after it was last finished or skipped.
 * - A feature tour (`since`) is offered once, to people who were already here
 *   before it shipped and have not taken the welcome tour since - the welcome
 *   tour covers everything that exists when it is taken.
 * - One offer at a time, welcome first, then the oldest feature tour.
 * - Any tour can be taken again from the ? button or Settings, any time.
 *
 * ── ADDING ONE ──
 * A new feature ships with a tour here: an id with its month, `since` set to
 * the release date, its steps. Nothing else - the offer follows from the rules.
 */
export interface TourStep {
  id: string;
  title: string;
  body: string;
  /** Lit when visible; absent (or hidden, as on a phone) puts the card in the middle. */
  target?: string;
  /** The page the step is on, when it is not where the person happens to be. */
  route?: string;
  /** [resource, action] the thing behind the step needs. */
  needs?: [string, 'view' | 'create' | 'edit' | 'manage'];
  /** Only for someone with more than one book. */
  manyBooks?: boolean;
}

export interface Tour {
  id: string;
  title: string;
  /** What the offer card says the tour is about. */
  pitch: string;
  /** A feature tour's release date (YYYY-MM-DD). Absent on the welcome tour. */
  since?: string;
  steps: TourStep[];
}

export const WELCOME_TOUR_ID = 'welcome';
export const REOFFER_AFTER_DAYS = 182;

const nav = (href: string) => `aside a[href="${href}"]`;

export const TOURS: Tour[] = [
  {
    id: WELCOME_TOUR_ID,
    title: 'Welcome to Fint',
    pitch: 'A two-minute walk through everything your role can open.',
    steps: [
      {
        id: 'hello',
        title: 'Welcome to Fint',
        body: 'Invoices, expenses, banking and GST for your business, in one place. This tour shows you around - only the parts you can use. Skip it any time; it is under the ? button whenever you want it back.',
      },
      {
        id: 'search',
        title: 'Search anything',
        body: 'An invoice number, a customer, a GSTIN, a reference or an amount - type it here. Press / from anywhere to jump in; Enter shows every result.',
        target: '[data-testid="search-bar"]',
      },
      {
        id: 'add',
        title: 'Add money in or out',
        body: 'The quickest way to record a payment you made or money you received - the same few taps on a phone.',
        target: '[data-testid="quick-add-entry"]',
        needs: ['expense', 'create'],
      },
      {
        id: 'overview',
        title: 'Overview',
        body: 'All your books added up, side by side - for when you run more than one business.',
        target: nav('/overview'),
        manyBooks: true,
      },
      {
        id: 'dashboard',
        title: 'Dashboard',
        body: 'Where this book stands today: what you are owed, what is overdue, what you spent and the cash on hand.',
        target: nav('/'),
      },
      {
        id: 'inbox',
        title: 'Inbox',
        body: 'What your AI agent has proposed and nobody has decided yet. Nothing counts until you confirm it here - and a wrong one is discarded from its row.',
        target: nav('/inbox'),
      },
      {
        id: 'clients',
        title: 'Clients',
        body: 'For accountants: every business you look after, and what each one needs from you.',
        target: nav('/clients'),
      },
      {
        id: 'review',
        title: 'Review',
        body: 'What Fint found worth a second look - a large expense with no receipt, a duplicate, a missing GSTIN - and the questions raised about them.',
        target: nav('/review'),
        needs: ['review', 'view'],
      },
      {
        id: 'gst',
        title: 'GST returns',
        body: 'GSTR-1 and GSTR-3B, worked out from the invoices and expenses already here, with anything to read before filing called out at the top.',
        target: nav('/gst'),
        needs: ['gstReturn', 'view'],
      },
      {
        id: 'invoices',
        title: 'Invoices',
        body: 'Raise, send and track invoices. A draft is yours until you send it; a payment against one marks it paid.',
        target: nav('/invoices'),
        needs: ['invoice', 'view'],
      },
      {
        id: 'proformas',
        title: 'Proformas',
        body: 'Quotes and advance requests - not a sale yet, and on no return until they become an invoice.',
        target: nav('/proformas'),
        needs: ['proforma', 'view'],
      },
      {
        id: 'customers',
        title: 'Customers',
        body: 'Everyone you invoice, what each owes you, and a statement to send them.',
        target: nav('/customers'),
        needs: ['customer', 'view'],
      },
      {
        id: 'payments',
        title: 'Payments',
        body: 'Money received - against invoices, net of any TDS - and money that came in with no invoice behind it.',
        target: nav('/payments'),
        needs: ['payment', 'view'],
      },
      {
        id: 'items',
        title: 'Items',
        body: 'What you sell, with its rate, HSN or SAC and tax - picked on an invoice instead of typed again.',
        target: nav('/items'),
        needs: ['item', 'view'],
      },
      {
        id: 'expenses',
        title: 'Expenses',
        body: 'What you spent, by category, with the bill attached. A refund is money back on a cost, never income.',
        target: nav('/expenses'),
        needs: ['expense', 'view'],
      },
      {
        id: 'vendors',
        title: 'Vendors',
        body: 'Who you pay, and what you have paid each of them, with their bills one click away.',
        target: nav('/vendors'),
        needs: ['vendor', 'view'],
      },
      {
        id: 'banking',
        title: 'Banking',
        body: 'Your accounts and every line on them. Import a statement and file each line - income, a refund, partner capital, a drawing.',
        target: nav('/banking'),
        needs: ['banking', 'view'],
      },
      {
        id: 'reports',
        title: 'Reports',
        body: 'Profit and loss, and where the money came from and went - by month, category, vendor or project.',
        target: nav('/reports'),
        needs: ['report', 'view'],
      },
      {
        id: 'projects',
        title: 'Projects',
        body: 'What each job earned and cost, when invoices and expenses are filed under one.',
        target: nav('/projects'),
        needs: ['project', 'view'],
      },
      {
        id: 'settings',
        title: 'Settings',
        body: 'Your business details, taxes, team and AI connections - and which version of Fint is running.',
        target: nav('/settings'),
      },
      {
        id: 'help',
        title: 'Take it again any time',
        body: 'This tour, and a short one for each new feature, live here. That is everything - welcome aboard.',
        target: '[data-testid="tour-help"]',
      },
    ],
  },
  {
    id: 'search-bar-2026-10',
    since: '2026-10-06',
    title: 'New: the search bar',
    pitch: 'Search moved to the top of every page.',
    steps: [
      {
        id: 'bar',
        title: 'Search from the top of any page',
        body: 'Type an invoice number, a customer, a GSTIN, a reference or an amount. Suggestions drop down as you type; press / from anywhere to jump in.',
        target: '[data-testid="search-bar"]',
      },
      {
        id: 'all',
        title: 'Every result, one Enter away',
        body: 'Enter without picking a suggestion opens every match, grouped by invoices, customers, expenses, bank lines and more.',
        target: '[data-testid="search-bar"]',
      },
    ],
  },
  {
    id: 'vendors-2026-10',
    since: '2026-10-06',
    title: 'New: Vendors',
    pitch: 'See who you pay, and what you have paid them.',
    steps: [
      {
        id: 'nav',
        title: 'Vendors',
        body: 'Everyone you pay - Hostinger, the landlord, a freelancer - with what you have paid each of them.',
        target: nav('/vendors'),
        needs: ['vendor', 'view'],
      },
      {
        id: 'new',
        title: 'Add a vendor, then mark their expenses',
        body: 'On a vendor’s page, the expenses that look like theirs are listed for you - tick them and mark them in one click. Their bills come with them.',
        target: '[data-testid="vendor-new"]',
        route: '/vendors',
        needs: ['vendor', 'create'],
      },
    ],
  },
];

export type Can = (resource: string, action: 'view' | 'create' | 'edit' | 'manage') => boolean;

/** The steps this person's role can use. */
export function stepsFor(tour: Tour, can: Can, manyBooks: boolean): TourStep[] {
  return tour.steps.filter((step) => (!step.needs || can(step.needs[0], step.needs[1])) && (!step.manyBooks || manyBooks));
}

const DAY = 86_400_000;

/**
 * The one tour to offer now, or null. Pure, so the rules are tested rather
 * than trusted (tours.test.ts).
 */
export function tourToOffer(state: TourState | undefined, can: Can, manyBooks: boolean, now: Date, tours: Tour[] = TOURS): Tour | null {
  if (!state || !state.offers) return null;
  const welcomeTour = tours.find((t) => t.id === WELCOME_TOUR_ID);
  const welcome = state.tours[WELCOME_TOUR_ID];
  if (welcomeTour && (!welcome || now.getTime() - Date.parse(welcome.at) >= REOFFER_AFTER_DAYS * DAY)) return welcomeTour;

  // A feature tour is for people who were here before it shipped and have not
  // taken (or skipped) the welcome tour since, which already covers it.
  const coveredUntil = Math.max(Date.parse(state.userCreatedAt), welcome ? Date.parse(welcome.at) : 0);
  const features = tours
    .filter((t) => t.since && !state.tours[t.id])
    .filter((t) => Date.parse(`${t.since}T23:59:59Z`) > coveredUntil)
    .filter((t) => stepsFor(t, can, manyBooks).length > 0)
    .sort((a, b) => (a.since ?? '').localeCompare(b.since ?? ''));
  return features[0] ?? null;
}
